import * as yup from 'yup';
import { APPLE_LOCATION_MESSAGE_MAX_CHARS } from '../utils/locationUtils';
import { IMAGE_CONFIG } from '../utils/imageVisibilityConfig';
import {
  STAMP_SHORT_VALUE_KEYS as stampShortValueKeys,
  STAMP_LONG_VALUE_KEYS as stampLongValueKeys,
  STAMP_INDEX,
  STAMP_BANNER_LABELS,
} from '../utils/stampCardConstants';

import {
  Platform,
  ImageField,
  TemplateType,
} from '../../../../store/types/template';
import { allowedDesignsForTemplateType } from '../utils/enhancedDesignEligibility';
import { ImagesState } from '../../../../store/types/template';
import { FEATURED_ACTION_TYPES } from '../utils/featuredActionTypes';

const colorSchema = yup.object({
  passBackgroundColor: yup
    .string()
    .required('Background colour is required')
    .matches(/^#[A-Fa-f0-9]{6}$/, 'Must be a valid hex colour'),
  titleFontColor: yup
    .string()
    .required('Label colour is required')
    .matches(/^#[A-Fa-f0-9]{6}$/, 'Must be a valid hex colour'),
  bodyFontColor: yup
    .string()
    .required('Text colour is required')
    .matches(/^#[A-Fa-f0-9]{6}$/, 'Must be a valid hex colour'),
});

const nfcSchema = yup.object({
  enabled: yup.boolean().required(),

  source: yup
    .string()
    .oneOf(['uuid', 'extId', 'custom'])
    .when('enabled', {
      is: true,
      then: (s) => s.required('NFC source is required'),
      otherwise: (s) => s.optional(),
    }),

  customValue: yup.string().when(['enabled', 'source'], {
    is: (enabled, source) => enabled && source === 'custom',
    then: (s) => s.required('Custom value is required when source is custom'),
    otherwise: (s) => s.optional(),
  }),

  googleNfc: yup.boolean().when('enabled', {
    is: true,
    then: (s) => s.required('Google NFC flag is required when enabled'),
    otherwise: (s) => s.optional(),
  }),
});

const barcodeSchema = yup.object({
  enabled: yup.boolean().required(),
  type: yup.string().oneOf(['qr', 'code128', 'aztec', 'pdf417']).required(),
  source: yup
    .string()
    .oneOf(['uuid', 'extId', 'custom'])
    .required('Barcode source is required'),
  value: yup.string().when(['enabled', 'source'], {
    is: (enabled, source) => enabled && source === 'custom',
    then: (s) => s.required('Barcode value is required when custom source'),
  }),
  displayText: yup.boolean(),
});

const APPLE_APP_STORE_REGEX =
  /^https:\/\/(apps|itunes)\.apple\.com\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?app\/(?:[^/\s]+\/)?id\d+/i;

const GOOGLE_PLAY_STORE_REGEX =
  /^https:\/\/play\.google\.com\/store\/apps\/details\/?\?(?:[^&]*&)*id=[a-zA-Z0-9._-]+/;

export const HTTPS_URL_REGEX = /^https:\/\/\S+$/;

const featuredActionSlotSchema = yup.object({
  type: yup
    .string()
    .nullable()
    .oneOf([null, ...FEATURED_ACTION_TYPES], 'Select a valid action type')
    .test('type-required-with-url', 'Select an action type', function (type) {
      if (type) return true;
      return !this.parent?.url;
    }),
  url: yup
    .string()
    .nullable()
    .test(
      'url-required-with-type',
      'Add a URL for this featured action',
      function (url) {
        if (url) return true;
        return !this.parent?.type;
      }
    )
    .matches(HTTPS_URL_REGEX, {
      message: 'URL must start with https://',
      excludeEmptyString: true,
    }),
});

const featuredActionsSchema = yup
  .object({
    one: featuredActionSlotSchema,
    two: featuredActionSlotSchema,
  })
  .test(
    'unique-featured-action-types',
    'Each featured action type can only be used once',
    function (value) {
      if (!value?.one?.type || !value?.two?.type) return true;
      if (value.one.type !== value.two.type) return true;
      return this.createError({
        path: `${this.path}.two.type`,
        message:
          'This action type is already used by the other featured action',
      });
    }
  );

const appleEnhancedDesignSchema = yup.object({
  type: yup.string().nullable().oneOf([null, 'eventTicket', 'generic']),
  images: yup
    .object({
      artwork: yup.string().nullable(),
      secondaryLogo: yup.string().nullable().optional(),
      venueMap: yup.string().nullable().optional(),
    })
    .when('type', {
      is: (type: string | null | undefined) => type != null,
      then: (s) =>
        s.shape({
          artwork: yup.string().required('Artwork is required'),
        }),
      otherwise: (s) => s,
    }),
  featuredActions: featuredActionsSchema.when('type', {
    is: (type: string | null | undefined) => type === 'generic',
    then: (s) => s,
    otherwise: (s) => s.strip(),
  }),
});

const appLinkingSchema = yup.object({
  appleAppStoreUrl: yup
    .array()
    .of(
      yup
        .string()
        .trim()
        .nullable()
        .test(
          'is-valid-or-empty',
          'Must be a valid Apple App Store URL (e.g., https://apps.apple.com/us/app/example/id123456789)',
          (value) => !value || value === '' || APPLE_APP_STORE_REGEX.test(value)
        )
    )
    .max(10, 'You can provide up to 10 Apple App Store URLs')
    .optional(),

  googleDisplayText: yup
    .string()
    .nullable()
    .max(25, 'Display text must be less than 25 characters'),
  googleAppPackageName: yup
    .string()
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional()
    .matches(
      /^[a-z]+(\.[a-z0-9_]+)+$/,
      'Must be a valid Google Play package name (e.g., com.example.app)'
    )
    .nullable(),

  googleAppPlayStoreUrl: yup
    .string()
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional()
    .matches(
      GOOGLE_PLAY_STORE_REGEX,
      'Must be a valid Google Play Store URL (e.g., https://play.google.com/store/apps/details?id=com.example.app)'
    ),

  googleWebUrl: yup
    .string()
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional()
    .matches(
      HTTPS_URL_REGEX,
      'Must be a valid HTTPS web URL (e.g., https://example.com)'
    ),
});

const locationSchema = yup.object({
  name: yup.string().required('Location name is required'),
  address: yup.string().optional(),
  latitude: yup
    .number()
    .required('Latitude is required')
    .notOneOf([0], 'Latitude is required'),
  longitude: yup
    .number()
    .required('Longitude is required')
    .notOneOf([0], 'Longitude is required'),
  radius: yup
    .number()
    .min(10, 'Radius must be greater than 10')
    .max(1000, 'Radius must be less than 1000')
    .optional(),
  message: yup
    .string()
    .nullable()
    .optional()
    .max(
      APPLE_LOCATION_MESSAGE_MAX_CHARS,
      `Notification message must be ${APPLE_LOCATION_MESSAGE_MAX_CHARS} characters or fewer`
    ),
});

const beaconSchema = yup.object({
  name: yup.string().required('Beacon name is required'),
  proximityUuid: yup.string().required('Proximity UUID is required'),
  major: yup
    .number()
    .nullable()
    .optional()
    .max(65535, 'Major ID must be less than 65535'),
  minor: yup
    .number()
    .nullable()
    .optional()
    .max(65535, 'Minor ID must be less than 65535'),
  message: yup.string().nullable().optional(),
});

const formFieldSchema = yup.object({
  _idTouched: yup.boolean().optional(),
  _labelTouched: yup.boolean().optional(),
  id: yup.string().when(['type', '_idTouched'], {
    is: (type: string, _idTouched: boolean) => !!type && _idTouched === true,
    then: (s) =>
      s
        .required('Connected pass field is required')
        .trim()
        .min(1, 'Connected pass field is required'),
    otherwise: (s) => s.optional(),
  }),
  label: yup.string().when(['type', '_labelTouched'], {
    is: (type: string, _labelTouched: boolean) =>
      !!type && _labelTouched === true,
    then: (s) =>
      s.required('Heading is required').trim().min(1, 'Heading is required'),
    otherwise: (s) => s.optional(),
  }),
  type: yup
    .string()
    .oneOf(['input', 'dropdown', 'date'], 'Field type is required')
    .required('Field type is required'),
  required: yup.boolean().optional(),
  extId: yup.boolean().optional(),
  validation: yup
    .object({
      name: yup.string().optional(),
      _patternTouched: yup.boolean().optional(),
      _messageTouched: yup.boolean().optional(),
      pattern: yup.string().when(['name', '_patternTouched'], {
        is: (name: string, _patternTouched: boolean) =>
          name === 'custom' && _patternTouched === true,
        then: (s) =>
          s
            .required('Custom regex is required')
            .trim()
            .min(1, 'Custom regex is required'),
        otherwise: (s) => s.optional(),
      }),
      message: yup.string().when(['name', '_messageTouched'], {
        is: (name: string, _messageTouched: boolean) =>
          name === 'custom' && _messageTouched === true,
        then: (s) =>
          s
            .required('Custom error message is required')
            .trim()
            .min(1, 'Custom error message is required'),
        otherwise: (s) => s.optional(),
      }),
    })
    .optional(),
  options: yup.array().when('type', {
    is: 'dropdown',
    then: (s) =>
      s
        .of(
          yup.object({
            id: yup.string().required(),
            _labelTouched: yup.boolean().optional(),
            label: yup.string().when('_labelTouched', {
              is: (touched: boolean) => touched === true,
              then: (s) =>
                s
                  .required('Option label is required')
                  .trim()
                  .min(1, 'Option label is required'),
              otherwise: (s) => s.optional(),
            }),
          })
        )
        .min(1, 'At least one option is required for dropdown')
        .required('At least one option is required'),
    otherwise: (s) => s.optional(),
  }),
  placeholder: yup.string().when('type', {
    is: 'dropdown',
    then: (s) =>
      s
        .required('Placeholder is required')
        .trim()
        .min(1, 'Placeholder is required'),
    otherwise: (s) => s.optional(),
  }),
  dateFormat: yup.string().when('type', {
    is: 'date',
    then: (s) =>
      s
        .default('%d/%m/%Y')
        .required('Date format is required')
        .trim()
        .min(1, 'Date format is required')
        .oneOf(
          ['%d/%m/%Y', '%Y/%m/%d', '%d/%m/%y', '%B %d, %Y'],
          'Invalid date format'
        ),
    otherwise: (s) => s.optional(),
  }),
});

const formSchema = yup.object({
  enabled: yup.boolean().required(),
  slugTouched: yup.boolean().optional(),
  slug: yup.string().when(['enabled', 'slugTouched'], {
    is: (enabled: boolean, slugTouched: boolean) =>
      enabled === true && slugTouched === true,
    then: (s) =>
      s
        .required('Form name is required')
        .trim()
        .min(1, 'Form name is required')
        .test(
          'unique-slug',
          'Form name must be unique',
          async function (value) {
            const validateSlug = this.options.context?.validateSlug;
            if (!validateSlug || !value?.trim()) return true;
            return validateSlug(value.trim());
          }
        ),
    otherwise: (s) => s.optional(),
  }),
  formFields: yup.array().of(formFieldSchema).optional(),
  backgroundColor: yup
    .string()
    .required('Background colour is required')
    .matches(/^#[A-Fa-f0-9]{6}$/, 'Must be a valid hex colour'),
  componentFillColor: yup
    .string()
    .required('Component colour is required')
    .matches(/^#[A-Fa-f0-9]{6}$/, 'Must be a valid hex colour'),
  componentFontColor: yup
    .string()
    .required('Font colour is required')
    .matches(/^#[A-Fa-f0-9]{6}$/, 'Must be a valid hex colour'),
  buttonText: yup
    .string()
    .required('Button text is required')
    .trim()
    .min(1, 'Button text is required'),
  buttonCtaColor: yup
    .string()
    .required('Button background colour is required')
    .matches(/^#[A-Fa-f0-9]{6}$/, 'Must be a valid hex colour'),
  buttonColor: yup
    .string()
    .required('Button background colour is required')
    .matches(/^#[A-Fa-f0-9]{6}$/, 'Must be a valid hex colour'),
  formLogoImage: yup.string().optional(),
  formFaviconImage: yup.string().optional(),
  formFaviconUrl: yup.string().optional(),
  footerEnabled: yup.boolean().optional(),
  footerContent: yup.string().when('footerEnabled', {
    is: true,
    then: (s) =>
      s
        .required('Footer text is required')
        .trim()
        .min(1, 'Footer text is required'),
    otherwise: (s) => s.optional(),
  }),
  footerColor: yup.string().when('footerEnabled', {
    is: true,
    then: (s) =>
      s
        .required('Footer text colour is required')
        .matches(/^#[A-Fa-f0-9]{6}$/, 'Must be a valid hex colour'),
    otherwise: (s) => s.optional(),
  }),
  queryParams: yup
    .array()
    .of(
      yup.object({
        _clientId: yup.string().required(),
        id: yup
          .string()
          .required('Connected field is required')
          .trim()
          .min(1, 'Connected field is required'),
        name: yup.string().optional(),
        required: yup.boolean().optional(),
      })
    )
    .optional(),
});

const fieldSchema = yup
  .object({
    id: yup
      .string()
      .test('id-required-if-present', 'Field ID is required', function (value) {
        // Skip when IDs are auto-generated on save
        if (this.options.context?.autoGenerateFieldId) return true;

        const parent = this.parent;
        const hasAnyProp =
          parent &&
          Object.values(parent).some(
            (v) => v !== '' && v !== null && v !== undefined
          );

        if (!hasAnyProp) return true;
        return !!(value && value.trim());
      })
      .test('unique-id', 'Duplicate Field ID', function (value) {
        // Skip when IDs are auto-generated on save
        if (this.options.context?.autoGenerateFieldId) return true;

        const all = this.options.context?.fields as any | undefined;
        if (!value || !all) return true;

        const fullPath = this.path;
        const relPath = fullPath.replace(/^fields\./, '');
        const val = String(value).trim();

        for (const [section, slots] of Object.entries(all || {})) {
          for (const [slot, f] of Object.entries((slots as any) || {})) {
            const otherPath = `${section}.${slot}.id`;
            if (otherPath === relPath) continue;
            const otherVal = (f as any)?.id;
            if (otherVal && String(otherVal).trim() === val) {
              return this.createError({
                path: fullPath,
                message: 'Duplicate Field ID',
              });
            }
          }
        }
        return true;
      }),
    label: yup.string().nullable(),
    defaultValue: yup
      .string()
      .nullable()
      .test(
        'valid-link-url',
        'URL must start with https://, http://, mailto: or tel:',
        function (value) {
          if (!value) return true;
          const anchorMatch = value.match(/<a\s+href=['"](.*?)['"]/);
          if (
            anchorMatch &&
            anchorMatch[1] &&
            !/^(https?:\/\/|mailto:|tel:)/i.test(anchorMatch[1])
          ) {
            return false;
          }
          return true;
        }
      ),
  })
  .test(
    'localisation-label-required',
    'Translated label is required',
    function (value) {
      const localisation = (
        value as {
          localisation?: Record<
            string,
            { label?: string; defaultValue?: string }
          >;
        }
      )?.localisation;
      if (!localisation || typeof localisation !== 'object') return true;

      const inner: yup.ValidationError[] = [];
      for (const [lang, entry] of Object.entries(localisation)) {
        const label = (entry?.label ?? '').trim();
        const defaultValue = (entry?.defaultValue ?? '').trim();
        if (defaultValue && !label) {
          inner.push(
            this.createError({
              path: `${this.path}.localisation.${lang}`,
              message: 'Translated label is required',
            }) as yup.ValidationError
          );
        }
      }
      if (inner.length > 0) throw new yup.ValidationError(inner);
      return true;
    }
  );

const nullableFieldSchema = yup.lazy((value) => {
  if (value === null || value === undefined) {
    return yup.mixed().nullable();
  }
  return fieldSchema;
});

const makeSectionSchema = () =>
  yup.object({
    one: nullableFieldSchema.optional(),
    two: nullableFieldSchema.optional(),
    three: nullableFieldSchema.optional(),
    four: nullableFieldSchema.optional(),
    five: nullableFieldSchema.optional(),
    six: nullableFieldSchema.optional(),
  });

export const fieldsSchema = yup.object({
  header: makeSectionSchema(),
  central: makeSectionSchema(),
  details: makeSectionSchema(),
  back: makeSectionSchema(),
});

const loyaltyBalanceSchema = yup.object({
  enabled: yup.boolean().required(),
  numberType: yup.string().oneOf(['integer', 'decimal']).required(),
});

const IMAGE_FIELDS = [
  'logo',
  'icon',
  'banner',
  'thumbnail',
  'wideLogo',
  'stampZeroImage',
  'stampOneImage',
  'stampTwoImage',
  'stampThreeImage',
  'stampFourImage',
  'stampFiveImage',
  'stampSixImage',
  'stampSevenImage',
  'stampEightImage',
  'stampNineImage',
  'stampTenImage',
  'stampElevenImage',
  'stampTwelveImage',
];

const getImageFieldRequiredMessage = (field: string): string => {
  const label = STAMP_BANNER_LABELS[field];
  if (label) return `${label} is required`;
  return `${field[0].toUpperCase() + field.slice(1)} is required`;
};

const makePlatformSchema = (platform: Platform) =>
  yup.object(
    Object.fromEntries(
      IMAGE_FIELDS.map((field) => [
        field,
        yup
          .string()
          .nullable()
          .test(
            'is-required-if-needed',
            getImageFieldRequiredMessage(field),
            function (value) {
              const { options } = this;
              const images = options?.context?.images as ImagesState;
              const templateType = options?.context?.templateType;
              const stampCount = options?.context?.stampCount as
                | number
                | undefined;
              const stampFeedbackTypes = options?.context
                ?.stampFeedbackTypes as string[] | undefined;

              if (!templateType || !images) return true;

              // Stamp card stamp images: only apply stamp-specific validation when changePassBanner is enabled
              const stampIndex = STAMP_INDEX[field];
              const isStampImageWithChangeBanner =
                stampIndex !== undefined &&
                templateType === 'stampCard' &&
                stampFeedbackTypes?.includes('changePassBanner');

              if (isStampImageWithChangeBanner) {
                const numericStampCount = Math.min(
                  Math.max(Number(stampCount) || 2, 2),
                  12
                );
                const requiredCount = numericStampCount + 1;
                if (stampIndex >= requiredCount) return true;

                const sharedHasImage = !!(
                  images.shared as Record<string, string>
                )[field];
                const platformImages = images[platform] as
                  | Record<string, string>
                  | undefined;
                const stampFieldsForCount = Object.keys(STAMP_INDEX).filter(
                  (f) => STAMP_INDEX[f] < requiredCount
                );
                const platformHasAnyStampImage =
                  platform !== 'shared' &&
                  stampFieldsForCount.some((f) => platformImages?.[f]);

                if (platform === 'shared') {
                  return !!value;
                }
                if (sharedHasImage) return true;
                if (!platformHasAnyStampImage) return true;
                return !!value;
              }

              // Stamp image fields when changePassBanner is off: no stamp-specific requirement (banner field is validated separately)
              if (
                stampIndex !== undefined &&
                templateType === 'stampCard' &&
                !stampFeedbackTypes?.includes('changePassBanner')
              ) {
                return true;
              }

              const fieldConfig =
                IMAGE_CONFIG[templateType]?.[platform]?.[field as ImageField];

              if (!fieldConfig?.visible) return true;

              const requiredIf = fieldConfig.requiredIf;

              if (!requiredIf?.unlessAllOf?.length) return true;

              const everyOverridePresent = requiredIf.unlessAllOf.every(
                (path) => {
                  const [p, f] = path.split('.') as [keyof ImagesState, string];
                  return !!images[p]?.[f];
                }
              );

              return everyOverridePresent ? true : !!value;
            }
          ),
      ])
    )
  );

export const imagesSchema = yup.object({
  shared: makePlatformSchema('shared'),
  apple: makePlatformSchema('apple'),
  google: makePlatformSchema('google'),
});

const makeStampShortValueFieldSchema = () =>
  yup
    .string()
    .optional()
    .nullable()
    .test(
      'required-when-short-key-set',
      'Updated field text is required',
      function (value) {
        const { options } = this;
        const root = options.context?.root;
        if (!root || root.templateType !== 'stampCard') return true;
        const shortKey = root.stampShortKey;
        if (shortKey == null || String(shortKey).trim() === '') return true;
        const pathParts = this.path?.split('.') ?? [];
        const key = pathParts[pathParts.length - 1];
        const index = stampShortValueKeys.indexOf(
          key as (typeof stampShortValueKeys)[number]
        );
        if (index < 0) return true;
        const numericCount = Math.min(
          Math.max(Number(root.stampCount) ?? 2, 2),
          12
        );
        const requiredCount = numericCount + 1;
        if (index >= requiredCount) return true;
        if (value != null && String(value).trim() !== '') return true;
        return this.createError({ message: 'Updated field text is required' });
      }
    );

const makeStampLongValueFieldSchema = () =>
  yup
    .string()
    .optional()
    .nullable()
    .test(
      'required-when-long-key-set',
      'Updated field text is required',
      function (value) {
        const root = (
          this.options.context as { root?: Record<string, unknown> }
        )?.root;
        if (!root || root.templateType !== 'stampCard') return true;
        const longKey = root.stampLongKey;
        if (longKey == null || String(longKey).trim() === '') return true;
        const pathParts = this.path?.split('.') ?? [];
        const key = pathParts[pathParts.length - 1];
        const index = stampLongValueKeys.indexOf(
          key as (typeof stampLongValueKeys)[number]
        );
        if (index < 0) return true;
        const numericCount = Math.min(
          Math.max(Number(root.stampCount) ?? 2, 2),
          12
        );
        const requiredCount = numericCount + 1;
        if (index >= requiredCount) return true;
        if (value != null && String(value).trim() !== '') return true;
        return this.createError({ message: 'Updated field text is required' });
      }
    );

const stampShortValuesSchema = yup.object(
  Object.fromEntries(
    stampShortValueKeys.map((key) => [key, makeStampShortValueFieldSchema()])
  )
);

const stampLongValuesSchema = yup.object(
  Object.fromEntries(
    stampLongValueKeys.map((key) => [key, makeStampLongValueFieldSchema()])
  )
);

const stampCardSchema = yup
  .object({})
  .test(
    'stamp-short-values-required',
    'Updated field text is required',
    function () {
      const root = this.parent as {
        templateType?: string;
        stampCount?: number;
        stampShortKey?: string;
        stampShortValues?: Record<string, string>;
      };
      if (!root || root.templateType !== 'stampCard') return true;
      const shortKey = root.stampShortKey;
      if (shortKey == null || String(shortKey).trim() === '') return true;
      const numericCount = Math.min(
        Math.max(Number(root.stampCount) ?? 2, 2),
        12
      );
      const requiredCount = numericCount + 1;
      const shortValues = root.stampShortValues ?? {};
      const inner: yup.ValidationError[] = [];
      for (let i = 0; i < requiredCount; i++) {
        const key = stampShortValueKeys[i];
        const val = shortValues[key];
        if (val == null || String(val).trim() === '') {
          inner.push(
            this.createError({
              path: `stampShortValues.${key}`,
              message: 'Updated field text is required',
            }) as yup.ValidationError
          );
        }
      }
      if (inner.length > 0) {
        throw new yup.ValidationError(inner);
      }
      return true;
    }
  )
  .test(
    'stamp-long-values-required',
    'Updated field text is required',
    function () {
      const root = this.parent as {
        templateType?: string;
        stampCount?: number;
        stampLongKey?: string;
        stampLongValues?: Record<string, string>;
      };
      if (!root || root.templateType !== 'stampCard') return true;
      const longKey = root.stampLongKey;
      if (longKey == null || String(longKey).trim() === '') return true;
      const numericCount = Math.min(
        Math.max(Number(root.stampCount) ?? 2, 2),
        12
      );
      const requiredCount = numericCount + 1;
      const longValues = root.stampLongValues ?? {};
      const inner: yup.ValidationError[] = [];
      for (let i = 0; i < requiredCount; i++) {
        const key = stampLongValueKeys[i];
        const val = longValues[key];
        if (val == null || String(val).trim() === '') {
          inner.push(
            this.createError({
              path: `stampLongValues.${key}`,
              message: 'Updated field text is required',
            }) as yup.ValidationError
          );
        }
      }
      if (inner.length > 0) {
        throw new yup.ValidationError(inner);
      }
      return true;
    }
  );

export const templateSchema = yup.object({
  templateName: yup
    .string()
    .required('Template name is required')
    .test('unique-name', 'Template name must be unique', function (value) {
      const isTemplateNameUnique = this.options.context?.isTemplateNameUnique;
      if (!isTemplateNameUnique || !value) return true;
      return isTemplateNameUnique(value.trim());
    }),
  templateType: yup.string().required('Please select a template type'),
  description: yup.string().required('Description is required'),
  centralTitle: yup.string().required('Central title is required'),
  notificationHeader: yup.string().required('Notification header is required'),
  transitType: yup
    .string()
    .oneOf(['airplane', 'bus', 'train', 'ferry', 'other'])
    .when('templateType', {
      is: 'transit',
      then: (s) => s.required('Transit type is required'),
      otherwise: (s) => s.optional().nullable(),
    }),
  // Nested structures
  colors: colorSchema.required(),
  images: imagesSchema.required(),
  nfc: nfcSchema,
  barcode: barcodeSchema,
  appleEnhancedDesign: appleEnhancedDesignSchema.when('templateType', {
    is: (templateType: TemplateType) =>
      allowedDesignsForTemplateType(templateType).length > 0,
    then: (s) => s,
    otherwise: (s) => s.optional().strip(),
  }),
  appLinking: appLinkingSchema,
  locations: yup.array().of(locationSchema).max(10),
  beacons: yup.array().of(beaconSchema).max(10),
  fields: fieldsSchema,
  loyaltyBalance: loyaltyBalanceSchema,
  form: formSchema.optional(),
  stampCard: stampCardSchema,
  stampShortValues: stampShortValuesSchema.optional(),
  stampLongValues: stampLongValuesSchema.optional(),
});
